import React, { useState, useEffect, useCallback, useRef } from "react";
import MatchCard from "./MatchCard";
import DatePickerCalendar from "./DatePickerCalendar";
import "./Partidos.css";
import "./DatePickerCalendar.css";
import { supabase } from "../supabaseClient";
import {
  addDays,
  endOfMonth,
  format,
  isValid,
  startOfMonth,
} from "date-fns";
import {
  formatDayLabel,
  formatGameDate,
  formatGameTime,
} from "../Functions/Formatting";
import { matchStatus } from "../Functions/matchStatus";
import { NavArrowButton, MatchListSkeleton } from "./MatchListParts";
import StateMessage from "./StateMessage";

const FRESH_MS = 30_000;
const PREFETCH_DELAY_MS = 300;

const CalendarIcon = () => (
  <svg
    className="date-picker-trigger-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);

const PartidosDia = () => {
  // cache: { all:YYYY-MM-DD: { data: Game[], ts: number } }
  const [gamesCache, setGamesCache] = useState({});
  const gamesCacheRef = useRef(gamesCache);
  gamesCacheRef.current = gamesCache;

  const [currentDayISO, setCurrentDayISO] = useState(
    () => `${format(new Date(), "yyyy-MM-dd")}T12:00:00.000Z`
  );
  const [error, setError] = useState(null);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [dateLoading, setDateLoading] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [markedDates, setMarkedDates] = useState(() => new Set());
  const [markedMonthKey, setMarkedMonthKey] = useState("");

  const dayStr = currentDayISO.split("T")[0];
  const cacheKey = `all:${dayStr}`;

  const fetchSupabaseGames = useCallback(async (d) => {
    const { data: rows, error } = await supabase
      .from("games")
      .select(
        "game_id:id, league_id, team1_id, team2_id, team1_score, team2_score, game_date, game_time, status, t1:teams!team1_id(team_name, logo_url, primary_kit_colors), t2:teams!team2_id(team_name, logo_url, primary_kit_colors), l:leagues!inner(league_name, theme_color)"
      )
      .eq("game_date", d)
      .eq("is_published", true)
      .eq("leagues.is_published", true)
      .order("game_time", { ascending: true })
      .order("id", { ascending: true });

    if (error) throw error;

    const formattedRows = (rows || [])
      .filter((row) => row.l)
      .map((row) => ({
      ...row,
      team1_name: row.t1?.team_name,
      team2_name: row.t2?.team_name,
      team1_logo_url: row.t1?.logo_url || null,
      team2_logo_url: row.t2?.logo_url || null,
      team1_kit_colors: row.t1?.primary_kit_colors || [],
      team2_kit_colors: row.t2?.primary_kit_colors || [],
      league_name: row.l.league_name,
      theme_color: row.l.theme_color || "#000000",
    }));

    formattedRows.sort((a, b) => {
      const nameA = a.league_name || "";
      const nameB = b.league_name || "";
      const timeA = a.game_time || "";
      const timeB = b.game_time || "";
      return nameA.localeCompare(nameB) || timeA.localeCompare(timeB);
    });
    return formattedRows;
  }, []);

  const fetchMonthMarkers = useCallback(async (monthStart) => {
    const start = format(startOfMonth(monthStart), "yyyy-MM-dd");
    const end = format(endOfMonth(monthStart), "yyyy-MM-dd");
    const key = start.slice(0, 7); // YYYY-MM
    setMarkedMonthKey(key);

    try {
      const { data, error } = await supabase
        .from("games")
        .select("game_date, leagues!inner(id)")
        .eq("is_published", true)
        .eq("leagues.is_published", true)
        .gte("game_date", start)
        .lte("game_date", end);

      if (error) throw error;

      const dates = new Set(
        (data || []).map((row) =>
          typeof row.game_date === "string"
            ? row.game_date.slice(0, 10)
            : format(new Date(row.game_date), "yyyy-MM-dd")
        )
      );
      setMarkedDates(dates);
    } catch (err) {
      console.error("Failed to load calendar markers", err);
    }
  }, []);

  const prefetchDay = useCallback(
    async (iso) => {
      if (!iso) return;
      const d = iso.split("T")[0];
      const key = `all:${d}`;
      const cached = gamesCacheRef.current[key];
      if (cached && Date.now() - cached.ts < FRESH_MS) return;

      try {
        const data = await fetchSupabaseGames(d);
        setGamesCache((prev) => {
          const existing = prev[key];
          if (existing && Date.now() - existing.ts < FRESH_MS) return prev;
          return { ...prev, [key]: { data, ts: Date.now() } };
        });
      } catch {
        // silent: prefetch is best-effort
      }
    },
    [fetchSupabaseGames]
  );

  useEffect(() => {
    if (!currentDayISO) return;

    let isMounted = true;
    const d = currentDayISO.split("T")[0];
    const key = `all:${d}`;
    const cached = gamesCacheRef.current[key];
    const now = Date.now();
    const hasCachedData = Array.isArray(cached?.data);

    // Only show a loading state when this day has nothing to display yet
    setDateLoading(!hasCachedData);
    if (hasCachedData) {
      setLoadingInitial(false);
    }

    (async () => {
      try {
        if (cached && now - cached.ts < FRESH_MS) {
          if (isMounted) {
            setLoadingInitial(false);
            setDateLoading(false);
          }
          return;
        }

        const data = await fetchSupabaseGames(d);
        // Always cache so rapid day-switching still benefits
        setGamesCache((prev) => ({
          ...prev,
          [key]: { data, ts: Date.now() },
        }));
        if (isMounted) {
          setLoadingInitial(false);
          setDateLoading(false);
        }
      } catch (err) {
        console.error("Daily (all leagues) fetch failed", err);
        if (isMounted) {
          if (!hasCachedData) setError("Failed to load daily games.");
          setLoadingInitial(false);
          setDateLoading(false);
        }
      } finally {
        if (isMounted) {
          prefetchDay(addDays(new Date(currentDayISO), 1).toISOString());
          prefetchDay(addDays(new Date(currentDayISO), -1).toISOString());
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [currentDayISO, fetchSupabaseGames, prefetchDay]);

  useEffect(() => {
    if (!currentDayISO) return;
    const id = setTimeout(() => {
      const base = new Date(currentDayISO);
      prefetchDay(addDays(base, 2).toISOString());
      prefetchDay(addDays(base, -2).toISOString());
    }, PREFETCH_DELAY_MS);
    return () => clearTimeout(id);
  }, [currentDayISO, prefetchDay]);

  const changeDay = (delta) => {
    setError(null);
    const newDate = addDays(new Date(currentDayISO), delta);
    if (isValid(newDate)) {
      setCurrentDayISO(`${format(newDate, "yyyy-MM-dd")}T12:00:00.000Z`);
    }
  };

  const handleMonthChange = useCallback(
    (monthStart) => {
      const key = format(monthStart, "yyyy-MM");
      if (key === markedMonthKey) return;
      fetchMonthMarkers(monthStart);
    },
    [fetchMonthMarkers, markedMonthKey]
  );

  const todayStr = format(new Date(), "yyyy-MM-dd");
  const isToday = dayStr === todayStr;
  const goToday = () => {
    setError(null);
    setCurrentDayISO(`${todayStr}T12:00:00.000Z`);
  };

  const gamesForToday = gamesCache[cacheKey]?.data || [];
  const showListLoading =
    (loadingInitial || dateLoading) && gamesForToday.length === 0;

  return (
    <div className="matchcard-container">
      <div className="period-navigation">
        <NavArrowButton
          direction="prev"
          label="Día anterior"
          onClick={() => changeDay(-1)}
        />

        <div className={`date-picker-shell${calendarOpen ? " open" : ""}`}>
          <button
            type="button"
            className="date-picker-trigger"
            onClick={() => setCalendarOpen((o) => !o)}
            aria-expanded={calendarOpen}
            aria-haspopup="dialog"
          >
            <span className="semana-text">
              {isToday ? (
                <>
                  <span className="semana-prefix">Hoy</span>{" "}
                  {formatGameDate(currentDayISO)}
                </>
              ) : (
                formatDayLabel(dayStr)
              )}
            </span>
            <CalendarIcon />
          </button>

          {calendarOpen && (
            <>
              <button
                type="button"
                className="date-picker-backdrop"
                aria-label="Cerrar calendario"
                onClick={() => setCalendarOpen(false)}
              />
              <DatePickerCalendar
                selectedISO={currentDayISO}
                markedDates={markedDates}
                onSelect={(iso) => {
                  setError(null);
                  setCurrentDayISO(iso);
                }}
                onMonthChange={handleMonthChange}
                onClose={() => setCalendarOpen(false)}
              />
            </>
          )}
        </div>

        <NavArrowButton
          direction="next"
          label="Día siguiente"
          onClick={() => changeDay(1)}
        />
      </div>

      {!isToday && (
        <button type="button" className="today-chip" onClick={goToday}>
          Volver a hoy
        </button>
      )}

      {dateLoading && gamesForToday.length > 0 && (
        <div className="subtle-badge" role="status">Actualizando…</div>
      )}

      {error ? (
        <StateMessage kind="error" title="No se pudieron cargar los partidos.">
          Revisa tu conexión e intenta de nuevo.
        </StateMessage>
      ) : showListLoading ? (
        <MatchListSkeleton rows={3} />
      ) : gamesForToday.length > 0 ? (
        gamesForToday.map((game) => (
          <MatchCard
            key={game.game_id}
            displayLeague={true}
            league={game.league_id}
            leagueName={game.league_name}
            themeColor={game.theme_color}
            team1={game.team1_name}
            team1Logo={game.team1_logo_url}
            team1KitColors={game.team1_kit_colors}
            team1Score={game.team1_score}
            team2={game.team2_name}
            team2Logo={game.team2_logo_url}
            team2KitColors={game.team2_kit_colors}
            team2Score={game.team2_score}
            time={formatGameTime(game.game_time)}
            date={formatGameDate(game.game_date)}
            status={matchStatus(game)}
            gameID={game.game_id}
          />
        ))
      ) : (
        <StateMessage title={isToday ? "No hay partidos hoy" : "Sin partidos este día"}>
          Usa las flechas o el calendario — los días con partidos aparecen
          marcados.
        </StateMessage>
      )}
    </div>
  );
};

export default PartidosDia;
